<script setup lang="ts">
/**
 * The free-tier attachment teaser (`/chat`, MAR-1387): a paperclip with a lock that opens an upgrade
 * popover instead of a file picker. The lock names no plan, since Lite, Pro and Ultra all attach (MAR-1420). Shared by both `ChatInput.vue` variants, which used to
 * carry two copies of it; `open` is a model because the composer also opens it on a drop or a paste.
 * The upgrade link shows only while billing can open a paid checkout (MAR-1474, `useCheckoutAvailability`); the
 * demo link always does.
 */
import { Lock, Paperclip } from 'lucide-vue-next'

defineProps<{ calcomUrl: string; triggerClass: string }>()
const open = defineModel<boolean>('open', { default: false })
const { t } = useI18n()
const { checkoutAvailable } = useCheckoutAvailability()
</script>

<template>
  <div class="relative">
    <button type="button" data-testid="chat-attach-pro-trigger" class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted cursor-pointer transition-colors" :class="triggerClass" :title="t('chat.proAttach.title')" :aria-label="t('chat.proAttach.title')" @click="open = !open">
      <Paperclip class="h-3.5 w-3.5" />
      <Lock data-attach-lock class="h-3 w-3 text-accent-ink" aria-hidden="true" />
    </button>
    <div v-if="open" data-testid="chat-attach-pro-popover" class="absolute bottom-full left-0 mb-2 w-80 rounded-lg border border-border bg-popover text-popover-foreground p-4 shadow-lg z-50 text-left">
      <p class="font-sans text-[13px] font-semibold text-primary mb-1">{{ t('chat.proAttach.title') }}</p>
      <p class="font-sans text-[12px] leading-relaxed text-muted-foreground mb-3">{{ t('chat.proAttach.body') }}</p>
      <div class="flex flex-wrap items-center gap-2">
        <NuxtLink v-if="checkoutAvailable" to="/app/setup" data-testid="chat-attach-pro-upgrade-cta" class="bg-accent text-on-accent hover:bg-accent/90 rounded-md px-3 py-1.5 text-xs font-medium transition-colors">{{ t('chat.proAttach.upgradeCta') }}</NuxtLink>
        <a :href="calcomUrl" target="_blank" rel="noopener noreferrer" data-testid="chat-attach-pro-demo-cta" class="border border-border bg-background text-primary hover:bg-accent-lighter rounded-md px-3 py-1.5 text-xs font-medium transition-colors">{{ t('chat.proAttach.demoCta') }}</a>
      </div>
    </div>
  </div>
</template>
